// Accesos de la vivienda según Sentinel: en qué puntos puede pasar y por qué, excepciones
// manuales (permitir aunque deba / bloquear) y dónde está cargado cada tag. Solo en
// comunidades con Sentinel. API: GET sentinel/units/{unit}/access.

import React, { useState } from 'react';
import { TextInput, TouchableOpacity, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../../../../../utils/api';
import tw from '../../../../../styles/tailwind';
import Text from '../../../../../components/Text';

type Exception = {
  uuid: string;
  effect: 'allow' | 'block';
  reason: string | null;
  expires_at: string | null;
  access_point: { uuid: string; name: string } | null;
};

type PointAccess = {
  uuid: string;
  name: string;
  kind: 'vehicular' | 'pedestrian' | 'amenity';
  zone: string | null;
  allowed: boolean;
  reason: 'exception' | 'delinquent' | null;
  delinquency_policy: 'ignore' | 'warn' | 'deny';
  exception: Exception | null;
};

type Deployment = {
  device: { uuid: string; name: string } | null;
  access_points: string[];
  desired: 'active' | 'inactive' | 'absent';
  applied: 'active' | 'inactive' | 'absent' | 'unknown';
  reason: string | null;
  in_sync: boolean;
};

type UnitAccess = {
  credentials: { uuid: string; type: string; code: string; label: string | null; status: string; deployments: Deployment[] }[];
  access_points: PointAccess[];
};

const KIND_ICON: Record<PointAccess['kind'], any> = {
  vehicular: 'car-outline',
  pedestrian: 'walk-outline',
  amenity: 'barbell-outline',
};

const EXPIRY = [
  { label: 'Sin vencimiento', days: null },
  { label: '7 días', days: 7 },
  { label: '30 días', days: 30 },
];

function stateOf(p: PointAccess): { label: string; color: string; bg: string } {
  if (p.exception?.effect === 'block') return { label: 'Bloqueado', color: '#D3123F', bg: '#FFE4EA' };
  if (p.exception?.effect === 'allow') return { label: 'Permitido por excepción', color: '#198077', bg: '#D4F7F4' };
  if (!p.allowed && p.reason === 'delinquent') return { label: 'Bloqueado por adeudo', color: '#D3123F', bg: '#FFE4EA' };
  return { label: 'Permitido', color: '#198077', bg: '#D4F7F4' };
}

const dateLabel = (iso: string) => new Date(iso).toLocaleDateString('es-MX', { day: 'numeric', month: 'short' });

export default function SentinelAccessPanel({ communityId, unitId, canManage }: { communityId: string; unitId: string; canManage: boolean }) {
  const queryClient = useQueryClient();
  const key = ['sentinel.unit-access', communityId, unitId];
  const base = `/communities/${communityId}/sentinel`;

  const { data, isLoading } = useQuery({
    queryKey: key,
    queryFn: () => api.get(`${base}/units/${unitId}/access`).then(r => r.data as UnitAccess),
  });

  const save = useMutation({
    mutationFn: (body: { access_point_id: string; effect: 'allow' | 'block'; reason: string; expires_at: string | null }) =>
      api.post(`${base}/units/${unitId}/exceptions`, body).then(r => r.data as UnitAccess),
    onSuccess: fresh => queryClient.setQueryData(key, fresh),
  });

  const remove = useMutation({
    mutationFn: (uuid: string) => api.delete(`${base}/exceptions/${uuid}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: key }),
  });

  const [open, setOpen] = useState<string | null>(null);

  if (isLoading || !data) {
    return <Text style={tw`text-gray-300 text-center py-4`}>Cargando accesos…</Text>;
  }

  const drift = data.credentials.flatMap(c => c.deployments.filter(d => !d.in_sync).map(d => ({ c, d })));

  return (
    <View>
      <View style={tw`row mb-2 mt-1`}>
        <Ionicons name="git-network-outline" size={18} color={tw.color('secondary')} />
        <Text style={tw`text-secondary font-600 ml-2`}>Puntos de acceso</Text>
      </View>

      <View style={tw`bg-white border border-primary/30 rounded-xl mb-3`}>
        {data.access_points.length === 0 && <Text style={tw`text-gray-400 text-center py-4`}>Sin puntos de acceso</Text>}
        {data.access_points.map((point, i) => (
          <PointRow
            key={point.uuid}
            point={point}
            first={i === 0}
            open={open === point.uuid}
            canManage={canManage}
            busy={save.isPending || remove.isPending}
            onToggle={() => setOpen(open === point.uuid ? null : point.uuid)}
            onSave={(effect, reason, days) => save.mutate(
              {
                access_point_id: point.uuid,
                effect,
                reason,
                expires_at: days ? new Date(Date.now() + days * 86_400_000).toISOString() : null,
              },
              { onSuccess: () => setOpen(null) },
            )}
            onRemove={uuid => remove.mutate(uuid, { onSuccess: () => setOpen(null) })}
          />
        ))}
      </View>

      {drift.length > 0 && (
        <View style={tw`bg-[#FFF3DC] rounded-xl px-4 py-3 mb-3`}>
          <Text style={tw`text-[#A15C00] font-600 text-sm mb-1`}>Tags por ajustar en los paneles</Text>
          {drift.map(({ c, d }, i) => (
            <Text key={i} style={tw`text-[#A15C00] text-xs`}>
              {c.label || c.code} · {d.device?.name}: {d.desired === 'active' ? 'debería estar activo' : 'debería estar desactivado'}
              {d.applied === 'absent' ? ' (no está cargado)' : ''}
            </Text>
          ))}
          <Text style={tw`text-[#A15C00] text-[11px] mt-1 opacity-80`}>
            Por ahora los paneles los sigue manejando el sistema actual.
          </Text>
        </View>
      )}
    </View>
  );
}

function PointRow({ point, first, open, canManage, busy, onToggle, onSave, onRemove }: {
  point: PointAccess;
  first: boolean;
  open: boolean;
  canManage: boolean;
  busy: boolean;
  onToggle: () => void;
  onSave: (effect: 'allow' | 'block', reason: string, days: number | null) => void;
  onRemove: (uuid: string) => void;
}) {
  const state = stateOf(point);
  const [reason, setReason] = useState('');
  const [days, setDays] = useState<number | null>(null);
  const blockedByDebt = !point.allowed && point.reason === 'delinquent';

  return (
    <View style={tw.style('px-4 py-3', !first && 'border-t border-gray-100')}>
      <TouchableOpacity style={tw`row`} onPress={canManage ? onToggle : undefined} disabled={!canManage}>
        <Ionicons name={KIND_ICON[point.kind]} size={18} color={tw.color('gray-400')} />
        <View style={tw`flex-1 ml-3`}>
          <Text style={tw`text-secondary font-500`}>{point.name}</Text>
          {point.zone && <Text style={tw`text-gray-400 text-xs`}>{point.zone}</Text>}
        </View>
        <View style={tw.style(`rounded-full px-2 py-0.5`, { backgroundColor: state.bg })}>
          <Text style={tw.style(`text-[11px] font-600`, { color: state.color })}>{state.label}</Text>
        </View>
        {canManage && <Ionicons name={open ? 'chevron-up' : 'chevron-down'} size={16} color={tw.color('gray-400')} style={tw`ml-2`} />}
      </TouchableOpacity>

      {open && point.exception && (
        <View style={tw`mt-3 bg-gray-50 rounded-lg p-3`}>
          <Text style={tw`text-gray-600 text-xs`}>
            {point.exception.effect === 'allow' ? 'Permitido a mano' : 'Bloqueado a mano'}
            {point.exception.reason ? ` · ${point.exception.reason}` : ''}
            {point.exception.expires_at ? ` · vence ${dateLabel(point.exception.expires_at)}` : ''}
          </Text>
          <TouchableOpacity style={tw`mt-2 self-start`} disabled={busy} onPress={() => onRemove(point.exception!.uuid)}>
            <Text style={tw`text-red font-600 text-sm`}>Quitar excepción</Text>
          </TouchableOpacity>
        </View>
      )}

      {open && !point.exception && (
        <View style={tw`mt-3`}>
          <TextInput
            value={reason}
            onChangeText={setReason}
            placeholder={blockedByDebt ? 'Motivo (ej. convenio de pago)' : 'Motivo (ej. mal uso)'}
            style={tw`bg-gray-50 rounded-lg px-3 py-2 text-sm text-secondary`}
          />
          <View style={tw`row flex-wrap mt-2`}>
            {EXPIRY.map(o => (
              <TouchableOpacity
                key={o.label}
                onPress={() => setDays(o.days)}
                style={tw.style('rounded-full px-3 py-1 mr-2 mb-1 border', days === o.days ? 'bg-selected border-selected-border' : 'border-gray-200')}
              >
                <Text style={tw`text-xs text-secondary`}>{o.label}</Text>
              </TouchableOpacity>
            ))}
          </View>
          <View style={tw`row mt-2`}>
            {blockedByDebt ? (
              <TouchableOpacity disabled={busy} onPress={() => onSave('allow', reason, days)} style={tw`bg-primary rounded-lg px-4 py-2`}>
                <Text style={tw`text-white font-600 text-sm`}>Permitir aunque deba</Text>
              </TouchableOpacity>
            ) : (
              <TouchableOpacity disabled={busy} onPress={() => onSave('block', reason, days)} style={tw`bg-red rounded-lg px-4 py-2`}>
                <Text style={tw`text-white font-600 text-sm`}>Bloquear este acceso</Text>
              </TouchableOpacity>
            )}
          </View>
        </View>
      )}
    </View>
  );
}
